import type { Variant } from '../../gameTypes.js';
import type { JumpCharGrid } from '../../jumpGrid.js';
import {
  remainingAnchors,
  tierAtLeast,
  TIER_RANK,
  type JumpAnchor,
  type JumpPattern,
  type JumpPatternContext,
} from './jumpPatternBase.js';
import { createJumpPatternRng, type JumpPatternRng } from './jumpPatternRng.js';
import { JumpStamper, PIT_MAX_COLS, PIT_MIN_COLS } from './jumpPatternStamp.js';
import {
  DoubleTapPattern,
  LoneHopPattern,
  SpikeGauntletPattern,
  SpikeWallPattern,
  TripleStutterPattern,
} from './jumpGroundPatterns.js';
import { AscendingStaircasePattern, StepUpRidePattern } from './jumpTerrainPatterns.js';
import { CorridorPattern, SawLeapPattern } from './jumpFeaturePatterns.js';

/** A break (onset-free stretch) must be at least this wide to host a pit. */
const PIT_MIN_GAP_BEATS = 3.5;

/** Per-tier chance a qualifying break carves a pit (indexed by {@link TIER_RANK}). */
const PIT_CHANCE_BY_TIER = [0.35, 0.55, 0.7];

/** Four-bar phrases end with a flat landing/recovery runway. */
const PHRASE_BEATS = 16;
const RECOVERY_BEATS_BY_TIER = [4, 3, 2];
const LANDING_BEATS = 1;

/**
 * The pattern registry. Order is irrelevant (selection is weighted), but it's
 * grouped by family for readability. Instantiated once — patterns are stateless
 * strategies, so a single shared instance per pattern is safe.
 */
/** Always-eligible pattern returned when (defensively) no other pattern qualifies. */
const FALLBACK_PATTERN = new LoneHopPattern();

export const JUMP_PATTERNS: readonly JumpPattern[] = [
  // Ground-spike family — the rhythmic staple.
  new LoneHopPattern(),
  new DoubleTapPattern(),
  new TripleStutterPattern(),
  new SpikeWallPattern(),
  new SpikeGauntletPattern(),
  // Terrain family — the platforming feel.
  new StepUpRidePattern(),
  new AscendingStaircasePattern(),
  // Feature family — readability / flourish hazards.
  new CorridorPattern(),
  new SawLeapPattern(),
];

function patternFits(pattern: JumpPattern, ctx: JumpPatternContext): boolean {
  return tierAtLeast(ctx.tier, pattern.minTier)
    && remainingAnchors(ctx) >= pattern.minAnchors
    && pattern.eligible(ctx);
}

/** Weighted selection among unlocked patterns that fit the remaining phrase. */
function selectPattern(ctx: JumpPatternContext, roll: number): JumpPattern {
  const pool: JumpPattern[] = [];
  const weights: number[] = [];
  let total = 0;
  for (const pattern of JUMP_PATTERNS) {
    if (!patternFits(pattern, ctx)) {
      continue;
    }
    const w = pattern.weight(ctx);
    // Reject non-positive AND non-finite (NaN/Infinity) weights so a malformed
    // `weight()` can't poison the running total or skew the weighted sample.
    if (!(w > 0) || !Number.isFinite(w)) {
      continue;
    }
    pool.push(pattern);
    weights.push(w);
    total += w;
  }
  if (pool.length === 0 || total <= 0) {
    return FALLBACK_PATTERN;
  }
  let target = roll * total;
  for (let i = 0; i < pool.length; i += 1) {
    target -= weights[i];
    if (target <= 0) {
      return pool[i];
    }
  }
  return pool[pool.length - 1];
}

/** Internal phrase gaps may carry a pit; intros, recoveries and outros stay flat. */
function carvePitsInBreaks(
  stamper: JumpStamper,
  anchors: readonly JumpAnchor[],
  variant: Variant,
  rng: JumpPatternRng,
): void {
  const chance = PIT_CHANCE_BY_TIER[TIER_RANK[variant]];
  for (let i = 1; i < anchors.length; i += 1) {
    const gapStart = anchors[i - 1].endBeat;
    const gapEnd = anchors[i].beat;
    if (gapEnd - gapStart < PIT_MIN_GAP_BEATS) {
      continue;
    }
    if (rng.feature() >= chance) {
      continue;
    }
    const width = PIT_MIN_COLS + Math.floor(rng.feature() * (PIT_MAX_COLS - PIT_MIN_COLS + 1));
    stamper.pit((gapStart + gapEnd) / 2, width);
  }
}

export interface ComposeJumpGridArgs {
  /** The mutable working grid (baseline floor already painted). */
  charGrid: JumpCharGrid;
  cols: number;
  /** The filtered onset spine (already ≥ `minGapBeats` apart). */
  anchors: readonly JumpAnchor[];
  totalBeats: number;
  cellBeats: number;
  longApexLead: number;
  shortApexLead: number;
  minGapBeats: number;
  maxClusterCols: number;
  variant: Variant;
  slug: string;
  inKiaiAt: (beat: number) => boolean;
}

/**
 * Compose a Jump grid by choreographing reusable PATTERNS onto the song's onset
 * spine: teach the jump heights, repeat motifs within four-bar phrases, then
 * leave flat recovery runway. Music sync is preserved — every
 * pattern anchors its required jumps to real onsets (apex-shifted by the stamper
 * so an on-beat press crests over each obstacle). Fully deterministic per (slug, variant).
 *
 * Clearability is NOT guaranteed here: terrain is conservative-by-construction,
 * but the caller MUST still run the grid through the reachability filter (which
 * drops any unthreadable spike/saw) before shipping.
 */
export function composeJumpGrid(args: ComposeJumpGridArgs): void {
  const {
    charGrid,
    cols,
    anchors,
    totalBeats,
    cellBeats,
    longApexLead,
    shortApexLead,
    minGapBeats,
    maxClusterCols,
    variant,
    slug,
    inKiaiAt,
  } = args;

  const rng = createJumpPatternRng(slug, variant);
  let taught = 0;
  let cursor = 0;
  while (cursor < anchors.length) {
    if (anchors[cursor].beat >= totalBeats) {
      break;
    }
    const teachingRemaining = Math.max(0, 3 - taught);
    const phraseEnd = Math.min(
      totalBeats,
      (Math.floor(anchors[cursor].beat / PHRASE_BEATS) + 1) * PHRASE_BEATS,
    );
    const obstacleEnd = phraseEnd - RECOVERY_BEATS_BY_TIER[TIER_RANK[variant]];
    const phraseAnchors: JumpAnchor[] = [];
    while (cursor < anchors.length && anchors[cursor].beat < phraseEnd) {
      const anchor = anchors[cursor++];
      if (anchor.beat + longApexLead + LANDING_BEATS < obstacleEnd) {
        phraseAnchors.push(anchor);
      }
    }
    const stamper = new JumpStamper(charGrid, cols, obstacleEnd, cellBeats, longApexLead, shortApexLead);
    // A phrase repeats a shape before the next phrase varies it. Keep its height
    // and twist rolls stable so repetition teaches a recognisable movement.
    const twist = rng.twist();
    const place = rng.place();
    const phraseRng: JumpPatternRng = { ...rng, twist: () => twist, place: () => place };
    let motif: JumpPattern | null = null;
    let index = 0;
    while (index < phraseAnchors.length) {
      const anchor = phraseAnchors[index];
      // Skip an onset whose obstacle column would fall outside the playable bounds
      // (right at the spawn or past the chart end) rather than paint nothing.
      if (!stamper.canPlaceCol(stamper.fullPressCol(anchor.beat))) {
        index += 1;
        continue;
      }
      // Teach the two jump heights in isolation before combining them with terrain.
      if (taught < 3) {
        if (taught < 2) {
          stamper.lowSpike(anchor.beat);
        } else {
          stamper.tallSpike(anchor.beat);
        }
        taught += 1;
        index += 1;
        continue;
      }
      const ctx: JumpPatternContext = {
        stamper,
        anchors: phraseAnchors,
        index,
        tier: variant,
        progress: totalBeats > 0 ? anchor.beat / totalBeats : 0,
        inKiai: inKiaiAt(anchor.beat),
        rng: phraseRng,
        minGapBeats,
        maxClusterCols,
        cellBeats,
        totalBeats: obstacleEnd,
      };
      const pattern: JumpPattern = motif !== null && patternFits(motif, ctx)
        ? motif
        : selectPattern(ctx, rng.select());
      motif = pattern;
      const placement = pattern.paint(ctx);
      index += Math.max(1, placement.consumed);
    }
    if (taught >= 3) {
      carvePitsInBreaks(stamper, phraseAnchors.slice(teachingRemaining), variant, rng);
    }
  }
}
